import { useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { GET_USERS } from '@atom/graph/user';
import { GET_WORK_ORDER } from '@atom/graph/work';
import { useNavigation } from '@atom/hooks/useNavigation';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ApprovalsSection from './ApprovalsSection';
import {
  ALTERNATIVES,
  RAMIFICATIONS,
  REASONS_FOR_OVERTIME,
} from './hidotOvertimeReportUtilities';
import LongTextSection from './LongTextSection';
import RoutingGrid from './RoutingGrid';
import TeamSection from './TeamSection';

import classes from './hidotOvertimeReport.module.css';

const HidotOvertimeReport = () => {
  const [, { params }] = useNavigation();
  const { workOrderId } = params;

  const {
    data: workOrderData,
    loading: workOrderLoading,
    error: workOrderError,
  } = useQuery(GET_WORK_ORDER, {
    variables: {
      id: workOrderId,
    },
    fetchPolicy: 'network-only',
  });

  const [
    getUsers,
    {
      data: usersData,
      loading: usersLoading,
      error: usersError,
      called: usersCalled,
    },
  ] = useLazyQuery(GET_USERS, {
    fetchPolicy: 'no-cache',
  });

  const workOrder = workOrderData?.workOrder;
  const task = R.head(workOrder?.tasks || []);

  const userIds = useMemo(
    () => R.uniq((task?.users || []).map(user => user?.id)),
    [task],
  );

  useEffect(() => {
    if (!isNilOrEmpty(userIds)) {
      getUsers({ variables: { input: { ids: userIds } } });
    }
  }, [userIds]);

  const usersSettled =
    isNilOrEmpty(userIds) || (usersCalled && !usersLoading && !usersError);
  const showLoaded = !workOrderLoading && !workOrderError && usersSettled;

  return (
    <div className={classes.container}>
      {task && (
        <>
          <div className={classes.taskTitle}>{task?.name}</div>
          <RoutingGrid workOrder={workOrder} task={task} />
          <TeamSection
            workOrder={workOrder}
            task={task}
            users={usersData?.users?.users}
          />
          <LongTextSection task={task} title={REASONS_FOR_OVERTIME} />
          <LongTextSection task={task} title={RAMIFICATIONS} />
          <LongTextSection task={task} title={ALTERNATIVES} />
          <ApprovalsSection task={task} />
        </>
      )}
      {showLoaded && <div className={classes.loaded} id="loaded" />}
    </div>
  );
};

export default HidotOvertimeReport;
